import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Add as AddIcon,
  Build as MaintenanceIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  ErrorOutline as ErrorOutlineIcon,
  Schedule as ScheduleIcon,
  Visibility as VisibilityIcon,
  ViewList as ViewListIcon,
  ViewKanban as ViewKanbanIcon,
} from '@mui/icons-material';
import MaintenanceKanban from './MaintenanceKanban';
import {
  MAINTENANCE_STATUS_FILTER_OPTIONS,
  getMaintenanceStatusMeta,
  resolveMaintenanceStatus,
} from '../../../utils/maintenanceStatus';
import { canManageMaintenanceWorkflow } from '../../../utils/maintenancePermissions';
import dayjs from 'dayjs';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { MAINTENANCE_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import { ACCENT, TABLE_SURFACE_2 } from '../../common/SaaSTokens';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns';
import { canModifyMaintenanceRequest, canArchiveMaintenanceRequest } from '../../../utils/maintenancePermissions';

const MaintenanceList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem('maintenanceListView') === 'kanban' ? 'kanban' : 'table';
    } catch {
      return 'table';
    }
  });
  const [updatingRequestId, setUpdatingRequestId] = useState(null);

  const filterOptions = [
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      options: MAINTENANCE_STATUS_FILTER_OPTIONS
    },
    {
      name: 'priority',
      label: 'Priority',
      type: 'select',
      options: [
        { value: 'low', label: 'Low' },
        { value: 'medium', label: 'Medium' },
        { value: 'high', label: 'High' },
        { value: 'urgent', label: 'Urgent' }
      ]
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      options: [
        { value: 'repair', label: 'Repair' },
        { value: 'replacement', label: 'Replacement' },
        { value: 'inspection', label: 'Inspection' },
        { value: 'other', label: 'Other' }
      ]
    }
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    // All authenticated users can access maintenance list
    fetchMaintenanceRequests({ forceRefresh: Boolean(location.state?.refreshList) });
  }, [currentUser, location.pathname, location.state?.refreshList]);

  const fetchMaintenanceRequests = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/maintenances', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setMaintenanceRequests(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching maintenance requests:', error);
      setError('Failed to load maintenance requests. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bulkHandlers = createBulkHandlers({
    endpoint: MAINTENANCE_BULK_CONFIG.endpoint,
    setItems: setMaintenanceRequests,
    selectedIds,
    clearSelection,
    setSuccess,
    successLabel: 'maintenance record(s)',
    refetch: fetchMaintenanceRequests,
    setError,
  });

  const handleArchive = (request) => {
    setSelectedRequest(request);
    setArchiveDialogOpen(true);
  };

  const confirmArchive = async () => {
    if (!selectedRequest) return;
    try {
      setArchiveLoading(true);
      await api.post(`/maintenances/${selectedRequest.id}/archive`);
      setMaintenanceRequests((prev) => prev.filter((r) => r.id !== selectedRequest.id));
      setArchiveDialogOpen(false);
      setSelectedRequest(null);
      setSuccess(`Maintenance request "${selectedRequest.title}" has been archived.`);
    } catch (err) {
      console.error('Error archiving maintenance:', err);
      setError(err?.response?.data?.message || 'Failed to archive maintenance request');
    } finally {
      setArchiveLoading(false);
    }
  };

  const handleRestore = async (request) => {
    try {
      await api.post(`/maintenances/${request.id}/restore`);
      setMaintenanceRequests((prev) => prev.filter((item) => item.id !== request.id));
      setSuccess(`Maintenance request "${request.title}" has been restored successfully.`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to restore maintenance request.');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    const nextActive = newFilters.status ? newFilters.status : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      if (newFilters.status === 'archived') setViewMode('table');
      fetchMaintenanceRequests({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({});
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchMaintenanceRequests({ forceRefresh: true });
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('maintenanceListView', mode);
    } catch {
      /* ignore */
    }
  };

  const handleKanbanStatusChange = async (request, newStatus) => {
    const prev = maintenanceRequests;
    const today = dayjs().format('YYYY-MM-DD');
    const optimistic = prev.map((r) =>
      r.id === request.id
        ? {
            ...r,
            status: newStatus,
            completion_date: newStatus === 'completed' ? (r.completion_date || today) : null,
          }
        : r
    );
    setMaintenanceRequests(optimistic);
    setUpdatingRequestId(request.id);
    try {
      const payload = {
        status: newStatus,
        completion_date: newStatus === 'completed' ? (request.completion_date || today) : null,
      };
      const { data } = await api.put(`/maintenances/${request.id}`, {
        title: request.title,
        description: request.description,
        location: request.location,
        type: request.type,
        priority: request.priority,
        assigned_to_id: request.assigned_to_id ?? null,
        ...payload,
      });
      setMaintenanceRequests((list) =>
        list.map((r) => (r.id === request.id ? data : r))
      );
      setSuccess(`Moved "${request.title}" to ${getMaintenanceStatusMeta(newStatus).label}`);
    } catch (err) {
      setMaintenanceRequests(prev);
      setError(err.response?.data?.message || 'Failed to update maintenance status');
    } finally {
      setUpdatingRequestId(null);
    }
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'pending', 'in_progress', 'completed'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      delete nextFilters.status;
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'pending' ? 1 :
    activeStatFilter === 'in_progress' ? 2 :
    activeStatFilter === 'completed' ? 3 :
    0;

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'low':
        return 'success';
      case 'medium':
        return 'info';
      case 'high':
        return 'warning';
      case 'urgent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'repair':
        return 'primary';
      case 'replacement':
        return 'secondary';
      case 'inspection':
        return 'info';
      case 'other':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <ErrorOutlineIcon fontSize="small" />;
      case 'high':
        return <FlagIcon fontSize="small" />;
      case 'medium':
        return <AccessTimeIcon fontSize="small" />;
      case 'low':
        return <CheckCircleIcon fontSize="small" />;
      default:
        return <FlagIcon fontSize="small" />;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon fontSize="small" />;
      case 'in_progress':
        return <ScheduleIcon fontSize="small" />;
      case 'pending':
        return <AccessTimeIcon fontSize="small" />;
      case 'archived':
        return <ArchiveIcon fontSize="small" />;
      default:
        return <AccessTimeIcon fontSize="small" />;
    }
  };

  const getMaintenanceInitials = (title) => {
    if (!title || typeof title !== 'string') {
      return 'MR'; // Default initials for maintenance requests
    }
    return title
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const filteredRequests = maintenanceRequests.filter(request => {
    const matchesSearch = 
      (request.title?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (request.description?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (request.location?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (request.reported_by?.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (request.assigned_to?.name?.toLowerCase() ?? '').includes(searchTerm.toLowerCase()) ||
      (request.assignees || []).some((member) => member.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (key === 'status') {
        return resolveMaintenanceStatus(request) === value;
      }
      if (key === 'priority') {
        return request.priority === value;
      }
      if (key === 'type') {
        return request.type === value;
      }
      return true;
    });

    return matchesSearch && matchesFilters;
  });

  /** Open / non-completed first; completed rows at the bottom (newest first within each group). */
  const sortedTableRows = useMemo(() => {
    // Sort rules:
    // 1) Not completed first (treat archived as resolved -> bottom)
    // 2) Urgent -> High -> Medium -> Low
    // 3) Newest reported_date first
    const completionRank = (status) => (status === 'completed' || status === 'archived' ? 1 : 0);
    const priorityRank = (priority) => (
      priority === 'urgent' ? 0
      : priority === 'high' ? 1
      : priority === 'medium' ? 2
      : priority === 'low' ? 3
      : 4
    );
    return [...filteredRequests].sort((a, b) => {
      const cr = completionRank(resolveMaintenanceStatus(a)) - completionRank(resolveMaintenanceStatus(b));
      if (cr !== 0) return cr;

      const pr = priorityRank(a.priority) - priorityRank(b.priority);
      if (pr !== 0) return pr;

      const ta = new Date(a.reported_date ?? a.created_at ?? a.updated_at ?? 0).getTime();
      const tb = new Date(b.reported_date ?? b.created_at ?? b.updated_at ?? 0).getTime();
      return tb - ta;
    });
  }, [filteredRequests]);

  const canModifyMaintenance = (maintenance) =>
    canModifyMaintenanceRequest(currentUser, maintenance);

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: MaintenanceIcon, value: maintenanceRequests.length, label: 'Total Requests', color: 'info' },
    { icon: AccessTimeIcon, value: maintenanceRequests.filter(r => !r.archived_at && r.status === 'pending').length, label: 'Pending', color: 'warning' },
    { icon: ScheduleIcon, value: maintenanceRequests.filter(r => !r.archived_at && r.status === 'in_progress').length, label: 'In Progress', color: 'secondary' },
    { icon: CheckCircleIcon, value: maintenanceRequests.filter(r => !r.archived_at && r.status === 'completed').length, label: 'Completed', color: 'success' },
  ];

  const maintenanceColumns = [
    {
      key: 'title',
      label: 'Request',
      icon: MaintenanceIcon,
      minWidth: 260,
      render: (request) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{request.title}</Typography>
          <Typography variant="caption" color="text.muted">{request.description || 'No description available'}</Typography>
        </Box>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      minWidth: 140,
      render: (request) => (
        <Chip
          icon={getPriorityIcon(request.priority)}
          label={request.priority?.charAt(0).toUpperCase() + request.priority?.slice(1) || 'Medium'}
          color={getPriorityColor(request.priority)}
          size="small"
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      render: (request) => {
        const status = resolveMaintenanceStatus(request);
        const isArchived = status === 'archived';
        return (
          <Chip
            icon={getStatusIcon(status)}
            label={status.split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            color={getStatusColor(status)}
            size="small"
            sx={isArchived ? {
              color: '#E2E8F0',
              bgcolor: 'rgba(148, 163, 184, 0.18)',
              border: '1px solid rgba(148, 163, 184, 0.45)',
              '& .MuiChip-icon': { color: '#CBD5E1' },
            } : undefined}
          />
        );
      },
    },
    {
      key: 'reported_by',
      label: 'Reported By',
      icon: PersonIcon,
      minWidth: 160,
      sortAccessor: (request) => request.reported_by?.name || '',
      render: (request) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {request.reported_by?.name || 'No reporter'}
        </Typography>
      ),
    },
    {
      key: 'reported_date',
      label: 'Reported Date',
      minWidth: 140,
      sortAccessor: (request) => request.reported_date || '',
      render: (request) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {request.reported_date ? format(new Date(request.reported_date), 'MMM dd, yyyy') : 'No date'}
        </Typography>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 170,
      render: (request) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Maintenance" arrow>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/maintenances/${request.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canModifyMaintenance(request) && (
            <>
              {!request.archived_at && <Tooltip title="Edit Maintenance" arrow>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/maintenances/${request.id}/edit`);
                  }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              {request.archived_at && (
                <Tooltip title="Restore Maintenance" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleRestore(request); }}
                    sx={{ color: theme.palette.success.main, '&:hover': { backgroundColor: alpha(theme.palette.success.main, 0.1) } }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canArchiveMaintenanceRequest(currentUser, request) && (
                <Tooltip title="Archive Maintenance" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchive(request);
                    }}
                    sx={{
                      color: theme.palette.error.main,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                      },
                    }}
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <FeedbackMessage message="Please log in to view this page." severity="error" />
      </Container>
    );
  }

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message='Loading maintenance list' size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Maintenance Management"
        subtitle="Track and manage facility maintenance requests and repairs"
        icon={MaintenanceIcon}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {[{ mode: 'table', Icon: ViewListIcon, label: 'List view' }, { mode: 'kanban', Icon: ViewKanbanIcon, label: 'Kanban view' }].map(({ mode, Icon, label }) => (
              <Tooltip key={mode} title={label} arrow>
                <IconButton
                  onClick={() => handleViewModeChange(mode)}
                  sx={{
                    borderRadius: '9px',
                    width: 36,
                    height: 36,
                    background: viewMode === mode ? alpha(ACCENT, 0.16) : alpha(TABLE_SURFACE_2, 0.5),
                    border: `1px solid ${alpha(ACCENT, viewMode === mode ? 0.4 : 0.15)}`,
                    color: viewMode === mode ? ACCENT : alpha(ACCENT, 0.5),
                    transition: 'all 0.2s ease',
                    '&:hover': { background: alpha(ACCENT, 0.1), borderColor: alpha(ACCENT, 0.35), color: ACCENT },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => navigate('/maintenances/add')}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)',
                },
                borderRadius: 2,
                px: 3,
                py: 1.5,
                alignSelf: { xs: 'stretch', sm: 'auto' }
              }}
            >
              Add Maintenance
            </Button>
          </Box>
        )}
      />

      {/* Error Message */}
      <FeedbackMessage message={error} severity="error" onClose={() => setError(null)} />
      <FeedbackMessage message={success} severity="success" onClose={() => setSuccess(null)} />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by title, description, location, reporter, or assignee..."
      />
      {viewMode === 'table' && filters.status !== 'archived' && (
        <ListBulkToolbar
          config={MAINTENANCE_BULK_CONFIG}
          currentUser={currentUser}
          selectedIds={selectedIds}
          onClearSelection={clearSelection}
          onSuccess={bulkHandlers.onSuccess}
          onDeleteSuccess={async () => {
            if (!selectedIds?.length) {
              setError('No selected maintenance records can be archived.');
              return;
            }
            try {
              await Promise.all(selectedIds.map((recordId) => (
                api.post(`/maintenances/${Number(recordId)}/archive`)
              )));
              const selectedIdSet = new Set(selectedIds.map(String));
              setMaintenanceRequests((prev) => (
                Array.isArray(prev)
                  ? prev.map((row) => (
                    selectedIdSet.has(String(row.id))
                      ? { ...row, archived_at: new Date().toISOString() }
                      : row
                  ))
                  : prev
              ));
              setError(null);
              setSuccess(`${selectedIds.length} maintenance record(s) archived successfully.`);
              clearSelection();
              fetchMaintenanceRequests({ forceRefresh: true });
            } catch (err) {
              setError(err?.response?.data?.message || 'Failed to archive selected maintenance requests.');
            }
          }}
        />
      )}

      {viewMode === 'table' || filteredRequests.length === 0 ? (
        <TableList
          columns={maintenanceColumns}
          data={sortedTableRows}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          {...(filters.status !== 'archived'
            ? bulkTableProps(MAINTENANCE_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)
            : {})}
          loading={loading}
          emptyState={{
            icon: <MaintenanceIcon />,
            title: 'No maintenance requests found',
            description: searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filters.'
              : 'Get started by creating your first maintenance request.',
          }}
          onRowClick={(row) => navigate(`/maintenances/${row.id}`)}
        />
      ) : (
        <MaintenanceKanban
          requests={filteredRequests}
          user={currentUser}
          canDrag={() => canManageMaintenanceWorkflow(currentUser)}
          onStatusChange={handleKanbanStatusChange}
          onOpenRequest={(r) => navigate(`/maintenances/${r.id}`)}
          onEditRequest={(r) => navigate(`/maintenances/${r.id}/edit`)}
          onArchiveRequest={handleArchive}
          updatingRequestId={updatingRequestId}
        />
      )}

      <ConfirmDeleteDialog
        open={archiveDialogOpen}
        onClose={() => {
          setArchiveDialogOpen(false);
          setSelectedRequest(null);
        }}
        onConfirm={confirmArchive}
        loading={archiveLoading}
        title="Archive Maintenance"
        itemType="maintenance request"
        itemName={selectedRequest?.title}
        description="The maintenance request will be archived and remain in the system for history and audit purposes."
        confirmLabel="Archive Maintenance"
        actionVerb="archive"
      />
    </Container>
  );
};

export default MaintenanceList;
